/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FlaskConical, 
    Zap, 
    Lock, 
    Unlock, 
    Wallet, 
    RefreshCw, 
    Activity, 
    Globe, 
    CreditCard, 
    ShieldAlert, 
    CheckCircle, 
    Cpu, 
    ArrowUpRight, 
    Flame,
    Database,
    Clock,
    Terminal,
    ChevronRight
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface DevTransaction {
    id: string;
    type: 'payment' | 'escrow' | 'wrap' | 'lock';
    amount: number;
    status: 'completed' | 'pending';
    time: string;
}

export const GhostCardDevView: React.FC = () => {
    // --- State Management ---
    const [isActive, setIsActive] = useState(true);
    const [activeNetwork, setActiveNetwork] = useState<'Sui Devnet' | 'Solana Devnet' | 'Ethereum Goerli'>('Sui Devnet');
    const [dailyLimit, setDailyLimit] = useState(500);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [simulatedTxCount, setSimulatedTxCount] = useState(0);
    const GHOST_LOGO_URL = "https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg";

    const [cardSettings, setCardSettings] = useState({
        cardholder: "DEV_TESTER_001",
        cardNumber: "•••• •••• •••• 1337",
        expiry: "01/29",
        pinRequired: true,
        biometricRequired: false
    });

    // --- Mock Data ---
    const transactions: DevTransaction[] = useMemo(() => [
        { id: 'dev_tx_1', type: 'payment', amount: 42.50, status: 'completed', time: '10:42 AM' },
        { id: 'dev_tx_2', type: 'lock', amount: 1000.00, status: 'completed', time: '09:15 AM' },
        { id: 'dev_tx_3', type: 'wrap', amount: 250.00, status: 'pending', time: 'Just now' },
    ], [simulatedTxCount]);

    const feeBreakdown = useMemo(() => {
        const amount = 100;
        const fee = amount * 0.025; // 2.5%
        return {
            total: amount,
            fee: fee.toFixed(2),
            burn: (fee * 0.6).toFixed(2),
            treasury: (fee * 0.4).toFixed(2)
        };
    }, []);

    // --- Handlers ---
    const handleSimulateAction = async (action: string) => {
        setIsProcessing(true);
        await new Promise(res => setTimeout(res, 2000));
        setIsProcessing(false);
        setShowSuccess(true);
        setSimulatedTxCount(prev => prev + 1);
        setTimeout(() => setShowSuccess(false), 4000);
    };

    return (
        <div className="space-y-8 animate-fade-in max-w-6xl mx-auto pb-20">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-end gap-6 border-b border-stone-200 dark:border-white/10 pb-8">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-nobel-gold/10 rounded-lg text-nobel-gold">
                            <FlaskConical size={24} />
                        </div>
                        <h2 className="font-serif text-3xl text-[#0A0B0F] dark:text-white font-bold">Ghost Card Titanium <span className="text-stone-400 font-light italic">Sandbox</span></h2>
                    </div>
                    <p className="text-stone-500 text-sm max-w-xl leading-relaxed">
                        Developer control plane for the Ghost Card ecosystem. Test cross-chain interactions, fee distributions, and governance mechanisms in a risk-free devnet environment.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant={isActive ? "primary" : "outline"} 
                        size="sm" 
                        onClick={() => setIsActive(!isActive)}
                        icon={isActive ? <Lock size={14}/> : <Unlock size={14}/>}
                        className="rounded-full px-6"
                    >
                        {isActive ? "ACTIVE" : "INACTIVE"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Visual & Core Controls */}
                <div className="lg:col-span-2 space-y-8">
                    
                    {/* The Card - Dev Variant */}
                    <div className="flex justify-center perspective-2000">
                        <motion.div 
                            animate={{ 
                                rotateY: isActive ? 0 : -10,
                                opacity: isActive ? 1 : 0.6,
                                filter: isActive ? 'grayscale(0%)' : 'grayscale(100%)'
                            }}
                            className={`relative w-full max-w-[460px] aspect-[1.586/1] bg-[#111] rounded-card p-10 flex flex-col justify-between text-white overflow-hidden shadow-2xl border ${isActive ? 'border-nobel-gold/30' : 'border-stone-800'}`}
                        >
                            {/* Materials */}
                            <div className="absolute inset-0 bg-gradient-to-br from-stone-800 via-stone-900 to-black"></div>
                            <div className="absolute inset-0 opacity-[0.05] bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')]"></div>
                            <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500 opacity-[0.05] blur-[80px]"></div>
                            
                            {/* Logo Watermark */}
                            <div className="absolute -left-12 -bottom-12 w-64 h-64 opacity-[0.03] rotate-12 pointer-events-none">
                                <img src={GHOST_LOGO_URL} alt="" className="w-full h-full grayscale invert" />
                            </div>

                            {/* TESTNET Watermark */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
                                <div className="text-8xl font-black font-mono rotate-[-15deg] select-none">TESTNET</div>
                            </div>

                            <div className="relative z-10 flex justify-between items-start">
                                <div className="space-y-4">
                                    <div className="w-14 h-9 rounded-[4px] bg-gradient-to-br from-stone-400 to-stone-600 border border-black/20 flex items-center justify-center">
                                        <div className="w-full h-[0.5px] bg-black/10"></div>
                                    </div>
                                    <div className="text-[10px] font-bold text-cyan-400 uppercase tracking-[0.4em]">Development Rails</div>
                                </div>
                                <div className="flex flex-col items-end">
                                    <div className="italic font-serif font-bold text-2xl text-white/90">Ghost</div>
                                    <div className="text-[9px] font-mono text-stone-500 uppercase tracking-tighter mt-1">Titanium Edition</div>
                                </div>
                            </div>

                            <div className="relative z-10">
                                <div className="font-mono text-2xl tracking-[0.3em] mb-6 drop-shadow-lg">{cardSettings.cardNumber}</div>
                                <div className="flex justify-between items-end">
                                    <div>
                                        <div className="text-[7px] uppercase tracking-[0.5em] text-stone-500 font-bold mb-1">TESTER IDENTITY</div>
                                        <div className="text-sm font-bold tracking-widest">{cardSettings.cardholder}</div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="text-right">
                                            <div className="text-[7px] uppercase tracking-[0.5em] text-stone-500 font-bold mb-1">EXPIRY</div>
                                            <div className="text-[10px] font-mono font-bold">{cardSettings.expiry}</div>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                                            <Globe size={18} className="text-stone-400" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Network & Environment Selector */}
                    <Card className="bg-[#0F1014] text-white border-white/5">
                        <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4">
                            <Globe size={18} className="text-blue-400" />
                            <h3 className="font-bold text-sm uppercase tracking-widest">Environment Management</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(['Sui Devnet', 'Solana Devnet', 'Ethereum Goerli'] as const).map(net => (
                                <button 
                                    key={net}
                                    onClick={() => setActiveNetwork(net)}
                                    className={`p-4 rounded-xl border text-xs font-bold transition-all ${activeNetwork === net ? 'bg-white text-black border-white shadow-xl scale-[1.02]' : 'bg-white/5 border-white/10 text-stone-500 hover:border-white/30'}`}
                                >
                                    {net}
                                </button>
                            ))}
                        </div>
                        
                        <div className="mt-6 flex items-center justify-between p-4 bg-black/40 rounded-xl border border-white/10">
                            <div className="flex items-center gap-3">
                                <Terminal size={14} className="text-stone-500" />
                                <span className="text-xs font-mono text-stone-400">Linked Dev Wallet:</span>
                                <span className="text-xs font-mono text-white">0x7611...b37c</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-8 text-[10px] font-bold">RE-AUTH</Button>
                        </div>
                    </Card>

                    {/* Simulation Actions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <h4 className="font-bold text-sm mb-4 flex items-center gap-2"><ArrowUpRight size={16} className="text-emerald-500"/> DeFi Simulations</h4>
                            <div className="grid grid-cols-1 gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleSimulateAction('wrap')} className="justify-between" icon={<Zap size={14}/>}>
                                    Wrap wGHOST (SOL) <ChevronRight size={12}/>
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleSimulateAction('unwrap')} className="justify-between" icon={<RefreshCw size={14}/>}>
                                    Unwrap GHOST (SUI) <ChevronRight size={12}/>
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleSimulateAction('lock')} className="justify-between" icon={<Lock size={14}/>}>
                                    Lock for veGHOST <ChevronRight size={12}/>
                                </Button>
                            </div>
                        </Card>
                        <Card>
                            <h4 className="font-bold text-sm mb-4 flex items-center gap-2"><Activity size={16} className="text-blue-500"/> System Stress Tests</h4>
                            <div className="grid grid-cols-1 gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleSimulateAction('latency')} className="justify-between" icon={<Clock size={14}/>}>
                                    Simulate Network Lag <ChevronRight size={12}/>
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleSimulateAction('error')} className="justify-between text-red-500 border-red-500/20" icon={<ShieldAlert size={14}/>}>
                                    Force Auth Timeout <ChevronRight size={12}/>
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Right Column: Insights & Metrics */}
                <div className="space-y-6">
                    {/* Spending Limits */}
                    <Card>
                        <h3 className="font-bold text-sm uppercase tracking-widest text-stone-500 mb-6">Sandbox Limits</h3>
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Daily Sandbox Limit</div>
                                    <div className="text-lg font-mono font-bold">${dailyLimit}</div>
                                </div>
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="5000" 
                                    step="100"
                                    value={dailyLimit} 
                                    onChange={(e) => setDailyLimit(parseInt(e.target.value))}
                                    className="w-full h-1.5 bg-stone-100 dark:bg-white/10 rounded-full appearance-none accent-nobel-gold cursor-pointer"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-stone-50 dark:bg-white/5 rounded-xl border border-stone-100 dark:border-white/10">
                                    <div className="text-[9px] text-stone-500 font-bold uppercase tracking-widest mb-1">Weekly</div>
                                    <div className="text-sm font-mono font-bold">$1,200.00</div>
                                </div>
                                <div className="p-3 bg-stone-50 dark:bg-white/5 rounded-xl border border-stone-100 dark:border-white/10">
                                    <div className="text-[9px] text-stone-500 font-bold uppercase tracking-widest mb-1">Monthly</div>
                                    <div className="text-sm font-mono font-bold">$4,500.00</div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Fee Model Visualization */}
                    <Card className="bg-[#0A0B0F] text-white border-white/10 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                            <Database size={100} className="rotate-12" />
                        </div>
                        <h3 className="font-bold text-xs uppercase tracking-widest mb-6 flex items-center gap-2">
                             <Flame size={14} className="text-orange-500" /> Protocol Fee Split (Sim)
                        </h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-stone-500">Input Amount</span>
                                <span className="font-mono font-bold">${feeBreakdown.total}.00</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-bold text-nobel-gold border-b border-white/5 pb-4">
                                <span>2.5% GHOST Fee</span>
                                <span className="font-mono">${feeBreakdown.fee}</span>
                            </div>
                            <div className="pt-2 space-y-3">
                                <div className="space-y-1">
                                    <div className="flex justify-between text-[10px] uppercase font-bold text-orange-500">
                                        <span>Permanent Burn (60%)</span>
                                        <span className="font-mono">${feeBreakdown.burn}</span>
                                    </div>
                                    <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                                        <div className="w-[60%] h-full bg-orange-500"></div>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex justify-between text-[10px] uppercase font-bold text-blue-400">
                                        <span>DAO Treasury (40%)</span>
                                        <span className="font-mono">${feeBreakdown.treasury}</span>
                                    </div>
                                    <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                                        <div className="w-[40%] h-full bg-blue-500"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Sandbox Log */}
                    <Card noPadding className="border-stone-200 dark:border-[#2D3142]">
                        <div className="p-4 border-b border-stone-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02] flex justify-between items-center">
                            <h3 className="text-xs font-bold uppercase tracking-widest text-stone-500">Sandbox Log</h3>
                            <button onClick={() => setSimulatedTxCount(0)} className="text-[10px] font-bold text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">CLEAR</button>
                        </div>
                        <div className="divide-y divide-stone-100 dark:divide-white/5">
                            {transactions.map(tx => (
                                <div key={tx.id} className="p-4 flex items-center justify-between group hover:bg-stone-50 dark:hover:bg-white/5 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-400 group-hover:text-nobel-gold transition-colors">
                                            <Terminal size={14}/>
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold text-[#0A0B0F] dark:text-white capitalize">{tx.type}</div>
                                            <div className="text-[9px] text-stone-500 font-mono">{tx.id}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs font-mono font-bold text-stone-800 dark:text-stone-200">
                                            {tx.type === 'payment' ? '-' : '+'}${tx.amount.toFixed(2)}
                                        </div>
                                        <div className={`text-[9px] font-bold uppercase tracking-widest ${tx.status === 'completed' ? 'text-green-500' : 'text-amber-500 animate-pulse'}`}>
                                            {tx.status}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Overlays */}
            <AnimatePresence>
                {isProcessing && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md">
                        <div className="text-center space-y-6 p-10 bg-[#0F1014] rounded-[40px] border border-white/10 shadow-2xl">
                            <div className="relative">
                                <div className="absolute inset-0 rounded-full border border-nobel-gold/30 animate-ping"></div>
                                <div className="w-20 h-20 rounded-full border-2 border-nobel-gold border-t-transparent animate-spin mx-auto"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Cpu size={24} className="text-nobel-gold"/>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-xl font-serif font-bold text-white mb-2">Executing Devnet Interaction</h3>
                                <p className="text-stone-500 text-xs font-mono uppercase tracking-[0.2em]">GhostOS Runtime Sequence Active</p>
                            </div>
                        </div>
                    </motion.div>
                )}

                {showSuccess && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="fixed bottom-10 right-10 bg-[#0A0B0F] text-white px-8 py-5 rounded-xl shadow-2xl flex items-center gap-4 z-50 border border-emerald-500/50">
                        <CheckCircle size={24} className="text-emerald-500" />
                        <div>
                            <div className="font-bold">Interaction Verified</div>
                            <div className="text-[10px] text-stone-400 font-mono tracking-tighter">SIG: 0x7611862f472f10b7410eb705e7e4e1160a0...</div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
